import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import React, { useMemo, useState } from "react"

import Typography from "../../../components/shared/Typography"

const SAMPLE_CLIENT = {
  client_first_name: "Alex",
  client_last_name: "Client",
  client_name: "Alex Client"
}

const mergeVariables = (content, practiceName) => {
  const values = {
    ...SAMPLE_CLIENT,
    practice_name: practiceName || "Your practice"
  }

  return Object.entries(values).reduce((html, [key, value]) => html.replaceAll(`{{${key}}}`, value), content || "")
}

const buildPreviewDocument = (htmlBody, practiceName) => {
  const body = mergeVariables(htmlBody, practiceName)
  const practiceLabel = practiceName || "Your practice"
  const footer = `
    <hr style="margin-top:24px;border:none;border-top:1px solid #e5e7eb;" />
    <p style="font-size:12px;color:#6b7280;">
      You are receiving this email from ${practiceLabel}.
      <a href="#" style="color:#0d9488;">Unsubscribe</a>
      ·
      <a href="#" style="color:#0d9488;">Manage preferences</a>
    </p>
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:24px;">
      <tr>
        <td align="center" style="padding:16px 0;">
          <a href="https://heal.me" target="_blank" rel="noopener noreferrer" style="text-decoration:none;color:#374151;">
            <img src="/images/HealmeLogo.png" alt="Heal.me" width="32" height="32" style="display:inline-block;vertical-align:middle;border-radius:8px;" />
            <span style="display:inline-block;vertical-align:middle;margin-left:8px;font-size:12px;font-weight:600;color:#374151;">
              Powered by <strong>Heal.me</strong>
            </span>
          </a>
        </td>
      </tr>
    </table>
  `

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        padding: 16px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 16px;
        line-height: 1.5;
        color: #111827;
        background: #ffffff;
        word-break: break-word;
      }
      img { max-width: 100%; height: auto; }
      a { color: #0d9488; }
      p { margin: 0 0 1em; }
    </style>
  </head>
  <body>
    ${body || "<p style='color:#9ca3af;'>Start writing your email to see a live preview.</p>"}
    ${footer}
  </body>
</html>`
}

const NewsletterEmailPreview = ({ subject, htmlBody, practiceName, fromEmail }) => {
  const [viewMode, setViewMode] = useState("desktop")
  const previewHtml = useMemo(() => buildPreviewDocument(htmlBody, practiceName), [htmlBody, practiceName])
  const previewSubject = useMemo(() => mergeVariables(subject, practiceName) || "(No subject)", [subject, practiceName])

  const frameClass = viewMode === "mobile" ? "h-[640px] w-[390px] max-w-full" : "h-[640px] w-full max-w-[600px]"

  return (
    <div className="rounded-xl border border-gray-light bg-gray-ultralight">
      <div className="flex items-center justify-between border-b border-gray-light bg-white px-4 py-3">
        <Typography variant="body" className="font-semibold">
          Live preview
        </Typography>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Desktop preview"
            className={`rounded p-2 ${
              viewMode === "desktop" ? "bg-teal text-white" : "text-gray-dark hover:bg-gray-ultralight"
            }`}
            onClick={() => setViewMode("desktop")}>
            <ComputerDesktopIcon className="size-5" />
          </button>
          <button
            type="button"
            aria-label="Mobile preview"
            className={`rounded p-2 ${
              viewMode === "mobile" ? "bg-teal text-white" : "text-gray-dark hover:bg-gray-ultralight"
            }`}
            onClick={() => setViewMode("mobile")}>
            <DevicePhoneMobileIcon className="size-5" />
          </button>
        </div>
      </div>

      <div className="border-b border-gray-light bg-white px-4 py-3 text-sm">
        <div className="truncate text-gray-dark">
          <span className="font-semibold text-black">From:</span> {fromEmail || "sender@example.com"}
        </div>
        <div className="mt-1 truncate">
          <span className="font-semibold">Subject:</span> {previewSubject}
        </div>
      </div>

      <div className="flex justify-center overflow-auto p-4">
        <div className={`${frameClass} overflow-hidden rounded-lg border border-gray-light bg-white shadow-md`}>
          <iframe
            title="Newsletter email preview"
            srcDoc={previewHtml}
            sandbox=""
            className="size-full border-0 bg-white"
          />
        </div>
      </div>

      <Typography variant="body" className="border-t border-gray-light px-4 py-2 text-center text-xs text-gray-dark">
        Preview uses sample name &quot;Alex Client&quot;. Personalization tokens update live.
      </Typography>
    </div>
  )
}

export default NewsletterEmailPreview
